import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  getTransactionHistory,
  clearTransactionHistory,
  clearError
} from "../store/slices/transactionSlice";
import { showApiError } from "../utils/toast";

export const useTransactionHistory = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { 
    transactionHistory,
    historyMeta,
    historyLoading,
    error
  } = useSelector((state) => state.transaction);

  const [filters, setFilters] = useState({
    filter: "TODAY",
    customDates: { from: "", to: "" },
    status: "all",
    search: "",
    page: 1,
    per_page: 15,
  });

  // Initial fetch on mount with TODAY filter
  useEffect(() => {
    fetchTransactions(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync URL params with filters for page changes only
  useEffect(() => {
    const currentPage = parseInt(searchParams.get("page") || "1", 10);
    if (currentPage !== filters.page) {
      const updatedFilters = {
        ...filters,
        page: currentPage
      };
      setFilters(updatedFilters);
      fetchTransactions(updatedFilters);
    }
  }, [searchParams]);

  // Handle errors
  useEffect(() => {
    if (error) {
      showApiError(null, error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(clearTransactionHistory());
    };
  }, [dispatch]);

  const fetchTransactions = useCallback(async (filtersToUse) => {
    const filtersParam = filtersToUse || filters;
    
    // Don't fetch if CUSTOM filter is selected but dates are incomplete
    if (filtersParam.filter === "CUSTOM" && (!filtersParam.customDates.from || !filtersParam.customDates.to)) {
      return;
    }
    
    const params = {};
    
    // Add filter parameter
    if (filtersParam.filter) {
      params.filter = filtersParam.filter;
      if (filtersParam.filter === "CUSTOM" && filtersParam.customDates.from && filtersParam.customDates.to) {
        params.from = filtersParam.customDates.from;
        params.to = filtersParam.customDates.to;
      }
    }
    
    if (filtersParam.status !== "all") {
      params.status = filtersParam.status.toUpperCase();
    }
    
    if (filtersParam.search.trim()) {
      params.search = filtersParam.search.trim();
    }
    
    // Use server-side pagination
    params.page = filtersParam.page;
    params.per_page = filtersParam.per_page;

    try {
      await dispatch(getTransactionHistory(params));
    } catch (error) {
      console.log('Transaction history fetch error:', error);
    }
  }, [dispatch, filters]);

  const updateFilter = (key, value) => {
    let newFilters;
    
    if (key === 'customDates') {
      // For customDates, update the nested object
      newFilters = {
        ...filters,
        customDates: value,
      };
    } else if (key === 'page') {
      // For page changes, don't reset page
      newFilters = {
        ...filters,
        [key]: value
      };
    } else {
      // For other filter changes, reset to page 1
      newFilters = {
        ...filters,
        [key]: value,
        page: 1
      };
    }
    
    setFilters(newFilters);
  };

  const resetFilters = () => {
    setFilters({
      filter: "TODAY",
      customDates: { from: "", to: "" },
      status: "all",
      search: "",
      page: 1,
      per_page: 15
    });
  };

  const onApplyFilter = useCallback(() => {
    // Reset page to 1 when applying new filter
    const filtersToApply = {
      ...filters,
      page: 1
    };
    setFilters(filtersToApply);
    fetchTransactions(filtersToApply);
  }, [filters, fetchTransactions]);

  const refreshTransactions = () => {
    fetchTransactions(filters);
  };

  // Calculate stats from current transactions
  const getStats = () => {
    const total = historyMeta?.total || transactionHistory.length;
    const fulfilled = transactionHistory.filter(t => t.settlement_status === 'Settled').length;
    const cancelled = 0;
    const initiated = 0;
    
    const totalRevenue = transactionHistory
      .reduce((sum, t) => sum + parseFloat(t.final_amount || 0), 0);
    
    const totalDiscounts = transactionHistory
      .reduce((sum, t) => sum + (parseFloat(t.customer_discount || 0) + parseFloat(t.platform_commission || 0)), 0);

    return { 
      total, 
      fulfilled, 
      cancelled, 
      initiated, 
      totalRevenue, 
      totalDiscounts 
    };
  };

  return {
    // State
    transactions: transactionHistory,
    meta: historyMeta,
    loading: historyLoading,
    filters,
    stats: getStats(),
    
    // Actions
    updateFilter,
    resetFilters,
    refreshTransactions,
    fetchTransactions,
    onApplyFilter
  };
};